import React from 'react';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import { ZoomIn } from 'lucide-react';
import { Button, Typography } from '@/components/common';
import { Input } from '@/components/form';
import { getCurrentFormatDate } from '@/utils';
import { format, parseISO } from 'date-fns';
import { HttpResponse, RequiredField, SignDocumentDetails } from '@/interfaces';
import { useNotification } from '@/hooks';
import { actionRequiredService } from '@/api';
import { useDebouncedCallback } from 'use-debounce';
import { ModalReviewSignFields } from './ModalReviewSignFields';
import { DeclineConfirmCard } from './DeclineConfirmCard';
import { useEditDocument } from '@/hooks/useEditDocument';

interface Props {
	actionDetails: HttpResponse<SignDocumentDetails> | undefined;
	actionRequiredId: number | undefined;
	isSignRequired: boolean | undefined;
	applicationId: number | undefined;
	isDocumentRead: boolean;
	documentUrl: string;
	refetch?: () => void;
	fields: RequiredField[];
	setIsModalOpen: (value: boolean) => void;
	setModalFooter?: (node: React.ReactNode) => void;
	resubmitFilesCount?: number;
	wasPreviouslyDeclined?: boolean;
}

export const ActionRequiredSection: React.FC<Props> = ({
	fields,
	refetch,
	documentUrl,
	actionDetails,
	applicationId,
	isSignRequired,
	isDocumentRead,
	setIsModalOpen,
	actionRequiredId,
	setModalFooter,
	resubmitFilesCount,
	wasPreviouslyDeclined = false,
}) => {
	const {
		getTrackingData,
		setPageInfoToPdf,
		getFieldQuestions,
		// uncomment if you need download the document to test
		// downloadSignedPdf
	} = useEditDocument();
	const { toastSuccess, toastError } = useNotification();
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isConfirmingDecline, setIsConfirmingDecline] = React.useState(false);

	const rules = {
		name: {
			required: { value: true, message: 'This field is required' },
			maxLength: {
				value: 120,
				message: 'Your name has to have less than 120 characters',
			},
			validate: (value: string) =>
				!!value?.trim() || 'Whitespace only is not allowed',
		},
		signature: {
			required: { value: true, message: 'This field is required' },
			maxLength: {
				value: 120,
				message: 'Your signature has to have less than 120 characters',
			},
			validate: (value: string) =>
				!!value?.trim() || 'Whitespace only is not allowed',
		},
	};

	const {
		register,
		getValues,
		watch,
		reset,
		setValue,
		formState: { isValid, errors },
		trigger,
	} = useForm({ mode: 'onChange', shouldUnregister: true });

	const isButtonEnable =
		(isSignRequired && isValid) || (!isSignRequired && isDocumentRead);

	React.useEffect(() => {
		const fieldValues = fields.reduce<Record<string, string>>((acc, field) => {
			if (field.response) acc[`field-${field.question_id}`] = field.response;
			return acc;
		}, {});

		reset({
			...fieldValues,
			...(actionDetails?.data.signature && {
				signature: actionDetails.data.signature,
			}),
			...(actionDetails?.data.signature &&
				actionDetails.data.signed_at && {
					date: format(parseISO(actionDetails.data.signed_at), 'MM/dd/yyyy'),
				}),
			firstName: actionDetails?.data.firstname,
			lastName: actionDetails?.data.lastname,
		});
	}, [actionDetails, fields]);

	const signature = watch('signature');

	const setTimeStamp = useDebouncedCallback(() => {
		const formattedDate = getCurrentFormatDate();
		setValue('date', formattedDate);
	}, 1000);

	// this is for the buttons in mobile
	React.useEffect(() => {
		if (!setModalFooter) return;

		setModalFooter(
			<div className="relative z-30 flex md:hidden gap-2 justify-between overflow-visible">
				<button type="button" onClick={handleDownloadDocument}>
					<ZoomIn className="text-purple-150 !size-6" />
				</button>

				{isSignRequired ? (
					<div className="flex items-center md:justify-end gap-2 lg:gap-4 w-full">
						<Button
							disabled={isSubmitting}
							decoration="danger-outline"
							onClick={() => setIsConfirmingDecline(true)}
							className="w-[161px] min-w-[161px] text-[16px] leading-[120%] !px-4"
						>
							Decline to Sign
						</Button>

						<Button
							disabled={!isButtonEnable || isSubmitting}
							decoration="primary"
							className="w-[106px] md:w-fit"
							onClick={() => handleSubmit(true)}
						>
							Accept
						</Button>
					</div>
				) : (
					<Button
						decoration="primary"
						size="fit"
						className="!h-12"
						disabled={
							fields.length > 0
								? !isDocumentRead || isSubmitting || !isValid
								: isSubmitting
						}
						onClick={() => handleSubmit(true)}
					>
						{fields.length > 0 ? 'Submit' : 'Done'}
					</Button>
				)}
				{declineConfirmOverlay}
			</div>
		);

		return () => setModalFooter(null);
	}, [
		setModalFooter,
		isSignRequired,
		isDocumentRead,
		isSubmitting,
		isConfirmingDecline,
		isButtonEnable,
		isValid,
		fields.length,
		documentUrl,
	]);

	React.useEffect(() => {
		if (fields.length === 0) return;
	}, [fields]);

	React.useEffect(() => {
		if (!signature) return;
		setTimeStamp();
	}, [signature]);

	const handleDownloadDocument = () => {
		window.open(documentUrl, '_blank');
	};

	const handleSubmit = async (isApprove: boolean) => {
		if (isApprove) trigger();
		if (!applicationId || !actionRequiredId) return;
		try {
			setIsSubmitting(true);
			const data = getValues();
			Object.keys(data).forEach((key) => {
				if (typeof data[key] === 'string') data[key] = data[key].trim();
			});
			const trackingData = await getTrackingData();
			const { answers, plaintiffInfo } = getFieldQuestions(data, fields);
			const signedPdfBlob = await setPageInfoToPdf(
				data,
				plaintiffInfo,
				isApprove,
				actionDetails?.data,
				!!resubmitFilesCount && resubmitFilesCount > 0
			);
			// uncomment if you need download the document to test
			// if (signedPdfBlob) {
			// 	await downloadSignedPdf(signedPdfBlob, 'documento-firmado.pdf');
			// }
			const params: any = {
				application_id: String(applicationId),
				action_id: String(actionRequiredId),
				signature: isApprove && data.signature ? data.signature : null,
				name: isApprove && data.name ? data.name : null,
				firstName: data.firstName ?? undefined,
				lastName: data.lastName ?? undefined,
				status: String(isApprove),
				...trackingData,
			};

			if (signedPdfBlob) {
				params.doc = signedPdfBlob;
			}

			if (answers && isApprove) {
				params.responses = JSON.stringify(answers);
			}

			if (isApprove && !isValid) return;
			await actionRequiredService.signDocument(params);
			toastSuccess(
				isApprove
					? 'The document has been signed successfully.'
					: wasPreviouslyDeclined
					? 'The required action has been successfully provided.'
					: 'The document has been successfully declined.'
			);
			refetch?.();
			setIsModalOpen(false);
		} catch (error) {
			toastError(
				error instanceof Error
					? error.message
					: 'An error has occurred. Please try again.'
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const declineConfirmOverlay =
		isSignRequired && isConfirmingDecline ? (
			<div className="pointer-events-none absolute inset-0 z-30 overflow-visible">
				<div className="pointer-events-auto absolute right-0 -bottom-2">
					<DeclineConfirmCard
						isSubmitting={isSubmitting}
						onCancel={() => setIsConfirmingDecline(false)}
						onConfirm={() => handleSubmit(false)}
					/>
				</div>
			</div>
		) : null;

	return (
		<div className="relative flex flex-col flex-1 h-full min-h-0 overflow-visible">
			{/* <HeaderNote />  */}
			<form className={clsx('flex flex-col flex-1 h-full min-h-0')}>
				<div
					className={clsx('space-y-4 flex-1 min-h-0', {
						'overflow-y-auto scroll-custom': isSignRequired,
					})}
				>
					{isSignRequired && (
						<div className=" space-y-4">
							<div className="flex items-baseline gap-4">
								<Input
									title="First name"
									name="firstName"
									register={register}
									rules={rules.name}
									error={errors.firstName}
									disabled={!isDocumentRead}
									className="w-full !text-left"
									classNameInput={clsx(
										`disabled:!bg-[#F4F4F4A3] !w-full !h-12`
									)}
									maxLength={100}
								/>
								<Input
									title="Last name"
									name="lastName"
									register={register}
									rules={rules.name}
									error={errors.lastName}
									disabled={!isDocumentRead}
									className="w-full !text-left"
									classNameInput={clsx(
										`disabled:!bg-[#F4F4F4A3] !w-full !h-12`
									)}
									maxLength={100}
								/>
							</div>
						</div>
					)}
					{/* Section Fields */}
					<div
						className={clsx(
							'max-h-[50vh] overflow-y-visible md:overflow-y-auto space-y-4 scroll-custom',
							{
								'!max-h-max': isSignRequired,
							}
						)}
					>
						<ModalReviewSignFields
							fields={fields}
							register={register}
							isDocumentRead={isDocumentRead}
							setValue={setValue}
							watch={watch}
							errors={errors}
						/>
					</div>
					{isSignRequired && (
						<>
							<Input
								title="Signature"
								name="signature"
								register={register}
								rules={rules.signature}
								error={errors.signature}
								classNameInput="!font-praise !text-2xl disabled:!bg-[#F4F4F4A3]  !h-12 placeholder:!font-sans"
								disabled={!isDocumentRead}
								maxLength={20}
								placeholder="/s/"
							/>
							<Input
								title="Date"
								name="date"
								register={register}
								disabled
								className="w-full"
								classNameInput="disabled:!bg-[#F4F4F4A3]  !h-12"
								placeholder="MM/DD/YYY"
							/>
							{!wasPreviouslyDeclined && (
								<div className="bg-grey-5 rounded-lg p-3">
									<Typography
										type="regular-14"
										className="!text-left text-opacity-black"
									>
										Your claim will be automatically rejected if you decline to
										sign this document.
									</Typography>
								</div>
							)}
						</>
					)}
				</div>

				<div className="hidden md:flex gap-2 md:justify-between mt-auto pt-6 shrink-0">
					<button type="button" onClick={handleDownloadDocument}>
						<ZoomIn className="text-purple-150 !size-6" />
					</button>
					{isSignRequired ? (
						<div className="flex items-center md:justify-end gap-2 lg:gap-4 w-full">
							<Button
								disabled={isSubmitting}
								decoration="danger-outline"
								onClick={() => setIsConfirmingDecline(true)}
								className="min-w-[170px] !w-fit text-[16px] leading-[120%]"
							>
								{`${isSignRequired ? 'Decline to Sign' : 'Decline'}`}
							</Button>

							<Button
								disabled={!isButtonEnable || isSubmitting}
								decoration="primary"
								className="w-[106px] md:w-fit"
								onClick={() => handleSubmit(true)}
							>
								Accept
							</Button>
						</div>
					) : (
						<Button
							decoration="primary"
							size="fit"
							className={clsx('!h-12', { 'opacity-60': !isValid })}
							disabled={
								fields.length > 0
									? !isDocumentRead || isSubmitting
									: isSubmitting
							}
							onClick={() => handleSubmit(true)}
						>
							{fields.length > 0 ? 'Submit' : 'Done'}
						</Button>
					)}
				</div>
			</form>
			<div className="hidden md:block">{declineConfirmOverlay}</div>
		</div>
	);
};
